import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber,
  Spinner, Button, useToast, useDisclosure, Drawer, DrawerOverlay,
  DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Badge, Icon, Input, InputGroup, InputLeftElement, NumberInput, NumberInputField,
  NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Flex
} from '@chakra-ui/react';
import { FiPackage, FiChevronRight, FiSearch, FiFilter, FiRefreshCw } from 'react-icons/fi';
import ModuleDashboard from './ModuleDashboard';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

export default function ModulesTab() {
  const [allModulesData, setAllModulesData] = useState([]); // Full dataset
  const [filteredModulesData, setFilteredModulesData] = useState([]); // Filtered dataset
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed at 10 rows per page
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [minIssueCount, setMinIssueCount] = useState('');
  const [maxIssueCount, setMaxIssueCount] = useState('');
  
  const toast = useToast();
  const { makeRequest } = useAnalyticsAPI();

  // Fetch modules data (limited to 10 items for performance)
  const fetchAllModulesData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching modules data (limited to 10 items)...');
      
      // Fetch only first page with 10 items limit
      const firstPageResult = await makeRequest('/api/bugs/analytics/individual-metrics', {
        query: {
          page: 1,
          limit: 10,
          includeDetails: true,
          includeSummary: true
        }
      });
      
      console.log('First page API Response:', firstPageResult);
      
      if (firstPageResult.status === 'success' && firstPageResult.data?.metrics) {
        let allIssuesData = [];
        
        // Process first page
        const processPageData = (pageData) => {
          const issuesData = [];
          if (pageData.metrics.issuesPerClient && Array.isArray(pageData.metrics.issuesPerClient)) {
            pageData.metrics.issuesPerClient.forEach(clientData => {
              if (clientData.issues && Array.isArray(clientData.issues)) {
                clientData.issues.forEach(issue => {
                  issuesData.push({
                    ...issue,
                    client: clientData.client || 'Unknown Client'
                  });
                });
              }
            });
          }
          return issuesData;
        };
        
        // Process first page
        allIssuesData = processPageData(firstPageResult.data);
        
        // Check if there are more pages
        const pagination = firstPageResult.metadata?.pagination;
        if (pagination && pagination.hasNext) {
          console.log(`Found ${pagination.totalPages} total pages, fetching remaining pages...`);
          
          // Fetch remaining pages
          for (let page = 2; page <= pagination.totalPages; page++) {
            console.log(`Fetching page ${page}...`);
            try {
              const nextPageResult = await makeRequest('/api/bugs/analytics/individual-metrics', {
                query: {
                  page: page,
                  limit: 100,
                  includeDetails: true,
                  includeSummary: true
                }
              });
              
              if (nextPageResult.status === 'success' && nextPageResult.data?.metrics) {
                const pageIssues = processPageData(nextPageResult.data);
                allIssuesData = [...allIssuesData, ...pageIssues];
                console.log(`Page ${page} loaded: ${pageIssues.length} issues`);
              }
            } catch (pageError) {
              console.error(`Failed to fetch page ${page}:`, pageError);
              // Continue with other pages even if one fails
            }
          }
        }
        
        // Derive modules data from issues
        const modulesMap = new Map();
        allIssuesData.forEach(issue => {
          const moduleName = issue.module || 'Unknown Module';
          if (modulesMap.has(moduleName)) {
            modulesMap.get(moduleName).count += 1;
            modulesMap.get(moduleName).issues.push(issue);
          } else {
            modulesMap.set(moduleName, {
              name: moduleName,
              count: 1,
              issues: [issue],
              // Add additional derived data
              severityDistribution: {},
              clients: new Set([issue.client])
            });
          }
          
          // Track severity distribution
          const severity = issue.severity || 'Unknown';
          const module = modulesMap.get(moduleName);
          module.severityDistribution[severity] = (module.severityDistribution[severity] || 0) + 1;
          module.clients.add(issue.client);
        });
        
        // Convert map to array and add derived properties
        const modulesData = Array.from(modulesMap.values()).map(module => ({
          ...module,
          uniqueClients: module.clients.size,
          clients: Array.from(module.clients),
          severityDistribution: module.severityDistribution,
          // Calculate average severity score
          avgSeverity: calculateAverageSeverity(module.severityDistribution)
        }));
        
        // Sort by count descending
        modulesData.sort((a, b) => b.count - a.count);
        
        console.log('All modules data derived:', modulesData.length, 'modules from', allIssuesData.length, 'issues');
        setAllModulesData(modulesData);
        
        toast({
          title: "Modules Data Loaded",
          description: `Loaded ${modulesData.length} modules from ${allIssuesData.length} issues`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.log('No modules data in response');
        setAllModulesData([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch modules data:', error);
      setAllModulesData([]);
      toast({
        title: "Error",
        description: `Failed to load modules data: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to calculate average severity
  const calculateAverageSeverity = (severityDistribution) => {
    const severityScores = {
      'CRITICAL': 4,
      'HIGH': 3,
      'MEDIUM': 2,
      'LOW': 1,
      'UNKNOWN': 0
    };
    
    let totalScore = 0;
    let totalIssues = 0;
    
    Object.entries(severityDistribution).forEach(([severity, count]) => {
      const score = severityScores[severity.toUpperCase()] || 0;
      totalScore += score * count;
      totalIssues += count;
    });
    
    return totalIssues > 0 ? (totalScore / totalIssues).toFixed(1) : 0;
  };

  // Apply filters to the full dataset
  const applyFilters = useMemo(() => {
    let filtered = [...allModulesData];
    
    console.log('Applying filters:', { searchTerm, minIssueCount, maxIssueCount });
    console.log('Initial data count:', filtered.length);
    
    // Search filter (case-insensitive) - search in module name
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(module => {
        const moduleName = module.name?.toLowerCase() || '';
        const matches = moduleName.includes(searchLower);
        console.log(`Module "${module.name}" matches search "${searchTerm}": ${matches}`);
        return matches;
      });
      console.log('After search filter:', filtered.length, 'modules');
    }
    
    // Issue count range filter
    if (minIssueCount !== '' && minIssueCount !== null) {
      const minCount = parseInt(minIssueCount);
      if (!isNaN(minCount)) {
        filtered = filtered.filter(module => {
          const matches = module.count >= minCount;
          console.log(`Module "${module.name}" (count: ${module.count}) >= ${minCount}: ${matches}`);
          return matches;
        });
        console.log('After min count filter:', filtered.length, 'modules');
      }
    }
    
    if (maxIssueCount !== '' && maxIssueCount !== null) {
      const maxCount = parseInt(maxIssueCount);
      if (!isNaN(maxCount)) {
        filtered = filtered.filter(module => {
          const matches = module.count <= maxCount;
          console.log(`Module "${module.name}" (count: ${module.count}) <= ${maxCount}: ${matches}`);
          return matches;
        });
        console.log('After max count filter:', filtered.length, 'modules');
      }
    }
    
    console.log('Final filtered count:', filtered.length, 'modules');
    return filtered;
  }, [allModulesData, searchTerm, minIssueCount, maxIssueCount]);

  // Update filtered data when filters change
  useEffect(() => {
    setFilteredModulesData(applyFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, [applyFilters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredModulesData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredModulesData.slice(startIndex, endIndex);
  
  const hasNext = currentPage < totalPages;
  const hasPrev = currentPage > 1;

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrev) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setMinIssueCount('');
    setMaxIssueCount('');
  };

  useEffect(() => {
    fetchAllModulesData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModuleClick = (module) => {
    setSelectedModule(module);
    onOpen();
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  if (loading && allModulesData.length === 0) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading modules data...</Text>
      </VStack>
    );
  }

  if (allModulesData.length === 0) {
    return (
      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack spacing={4} py={12} textAlign="center">
              <Icon as={FiPackage} size="3rem" color="gray.400" />
              <Text fontSize="xl" fontWeight="bold" color="gray.600">
                No Modules Data Available
              </Text>
              <Text color="gray.500" maxW="md">
                No modules data found. This could mean:
                <br />• Tickets are still being processed by the AI system
                <br />• No tickets have been submitted for analysis
                <br />• The API is not returning module analytics data
              </Text>
              <HStack spacing={4}>
                <Button
                  leftIcon={<Icon as={FiPackage} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={fetchAllModulesData}
                  isLoading={loading}
                >
                  Retry Loading
                </Button>
                <Button
                  leftIcon={<Icon as={FiRefreshCw} />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => {
                    toast({
                      title: "Processing Status",
                      description: "Check the 'Process All Tickets' button on the Overview tab to see processing status",
                      status: "info",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                >
                  Check Processing Status
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Modules</StatLabel>
              <StatNumber color="blue.500">{allModulesData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Filtered Results</StatLabel>
              <StatNumber color="green.500">{filteredModulesData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Issues</StatLabel>
              <StatNumber color="red.500">
                {allModulesData.reduce((sum, module) => sum + module.count, 0)}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filter Options */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Icon as={FiFilter} color="blue.500" />
              <Text fontSize="lg" fontWeight="bold">Filter Options</Text>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {/* Search Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Search Module Name</Text>
                <InputGroup>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search modules..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              
              {/* Min Issue Count Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Min Issue Count</Text>
                <NumberInput
                  min={0}
                  value={minIssueCount}
                  onChange={(value) => setMinIssueCount(value)}
                >
                  <NumberInputField placeholder="Min issues" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
              
              {/* Max Issue Count Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Max Issue Count</Text>
                <NumberInput
                  min={0}
                  value={maxIssueCount}
                  onChange={(value) => setMaxIssueCount(value)}
                >
                  <NumberInputField placeholder="Max issues" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
            </SimpleGrid>
            
            {/* Clear Filters Button */}
            {(searchTerm || minIssueCount || maxIssueCount) && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={clearFilters}
                size="sm"
                alignSelf="flex-start"
              >
                Clear All Filters
              </Button>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Modules Table */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiPackage} color="blue.500" />
                <Text fontSize="lg" fontWeight="bold">Modules</Text>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredModulesData.length)} of {filteredModulesData.length} modules
              </Text>
            </HStack>
            
            {/* Table */}
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Module Name</Th>
                    <Th isNumeric>Issue Count</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPageData.map((module) => (
                    <Tr 
                      key={module.name} 
                      _hover={{ bg: "gray.50", cursor: "pointer" }}
                      onClick={() => handleModuleClick(module)}
                    >
                      <Td fontWeight="medium">{module.name}</Td>
                      <Td isNumeric>
                        <Badge colorScheme={getSeverityColor(module.count > 10 ? 'high' : module.count > 5 ? 'medium' : 'low')}>
                          {module.count}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          rightIcon={<Icon as={FiChevronRight} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleModuleClick(module);
                          }}
                        >
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" spacing={4}>
                <Button
                  size="sm"
                  onClick={handlePrevPage}
                  isDisabled={!hasPrev}
                  variant="outline"
                >
                  Previous
                </Button>
                
                <HStack spacing={2} mx={4}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "solid" : "outline"}
                      colorScheme={currentPage === page ? "blue" : "gray"}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  ))}
                </HStack>
                
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={!hasNext}
                  variant="outline"
                >
                  Next
                </Button>
              </Flex>
            )}
            
            {/* No Results Message */}
            {filteredModulesData.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No modules match the current filters.</Text>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  onClick={clearFilters}
                  mt={2}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Module Dashboard Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack>
              <Icon as={FiPackage} color="blue.500" />
              <Text>{selectedModule?.name} - Module Dashboard</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            {selectedModule && (
              <ModuleDashboard module={selectedModule} onClose={onClose} />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
}
